"use client";
import { Button } from "@/components/ui/Button";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import {
  configurationSchema,
  createValidator,
  extractDefaultValues,
  getDisplayText,
  SELECTION_TYPES,
} from "@/schemas/genericSelectionSchema";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";

/**
 * Translation helper
 * Önce key varsa onu kullan, yoksa fallback text'i kullan
 */
const getTranslatedText = (text, textKey, t) => {
  if (!t) return text; // Translation function yoksa direkt text'i döndür
  if (textKey) {
    try {
      return t(textKey);
    } catch (error) {
      console.warn(`Translation key not found: ${textKey}`, error);
      return text || textKey;
    }
  }
  return text;
};

/**
 * GenericSelectionModal
 * Genel amaçlı seçim modal/dropdown componenti
 * Otobüs, uçak, otel gibi farklı ürünlerde kullanılabilir
 *
 * @param {Object} config - Configuration object (schema'ya uygun)
 * @param {String} label - Input label
 * @param {String} placeholder - Input placeholder
 * @param {Object} value - Current values
 * @param {Function} onSelect - Callback when apply button clicked
 * @param {Function} displayFormatter - Custom display text formatter
 * @param {Boolean} isMultiTrip - Multi trip styling flag
 * @param {Object} externalUpdates - External updates to values (from other components)
 * @param {String} translationNamespace - Translation namespace (e.g., "bus", "flight")
 * @param {String} applyButtonText - Apply button text (or translation key)
 * @param {('top'|'bottom')} generalErrorsPosition - Where to render general validation errors (default: 'top')
 */
const GenericSelectionModal = forwardRef(
  (
    {
      config,
      label,
      placeholder = "Seçim yapın",
      value = {},
      onSelect,
      onChange,
      displayFormatter,
      isMultiTrip = false,
      externalUpdates = {},
      applyButtonText = "Uygula",
      renderHeader,
      headerClassName = "",
      renderFooter,
      showTitle = true,
      showApplyButton = true,
      generalErrorsPosition = "top",
      className,
      inline = false,
      selectionItemsClassName,
      footerClassName,
      buttonWrapperClassName,
      onDropdownOpen,
      onDropdownClose,
      scrollToForm = false,
      onApplyValidationError,
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const t = useTranslationDetector();
    // Validate config
    const validatedConfig = useMemo(() => {
      try {
        return configurationSchema.parse(config);
      } catch (error) {
        console.error("Invalid configuration:", error);
        return { children: [] };
      }
    }, [config]);

    // Create validator
    const validator = useMemo(
      () => createValidator(validatedConfig),
      [validatedConfig],
    );

    // Extract default values from config
    const defaultValues = useMemo(
      () => extractDefaultValues(validatedConfig),
      [validatedConfig],
    );

    // Merge external value with defaults
    const initialValues = useMemo(
      () => ({ ...defaultValues, ...value }),
      [defaultValues, value],
    );

    // Temporary state - modal açıkken değişir
    const [tempValues, setTempValues] = useState(initialValues);

    // Actual confirmed state - "Uygula" butonuna basınca güncellenir
    const [confirmedValues, setConfirmedValues] = useState(initialValues);

    const [isOpen, setIsOpen] = useState(inline ? true : false);
    const [validationErrors, setValidationErrors] = useState({});
    const [validationWarnings, setValidationWarnings] = useState({});
    // Portal ve animasyon state'leri
    const [shouldRenderPortal, setShouldRenderPortal] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    // Web dropdown animasyon state'leri
    const [shouldRenderWebDropdown, setShouldRenderWebDropdown] =
      useState(false);
    const [isWebDropdownAnimating, setIsWebDropdownAnimating] = useState(false);

    const escapePressed = useKeyPressed("Escape");
    const containerRef = useRef(null);

    // Scroll to form when modal is opened
    useEffect(() => {
      if (!isMobile && scrollToForm && isOpen) {
        const inputRect = containerRef?.current?.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 200;
        window.scrollTo({ top: targetPosition, behavior: "smooth" });
      }
    }, [scrollToForm, isOpen, isMobile]);

    // Handle external updates
    useEffect(() => {
      if (Object.keys(externalUpdates).length > 0) {
        setTempValues((prev) => ({ ...prev, ...externalUpdates }));
        setConfirmedValues((prev) => ({ ...prev, ...externalUpdates }));
      }
    }, [externalUpdates]);

    const handleClickOutside = useCallback(() => {
      if (inline) return;
      if (!isMobile && isOpen) {
        setIsOpen(false);
        setTempValues(confirmedValues);
      }
    }, [inline, isMobile, isOpen, confirmedValues]);

    useEffect(() => {
      if (inline) return;
      if (escapePressed && isOpen) {
        setIsOpen(false);
        setTempValues(confirmedValues);
      }
    }, [escapePressed, isOpen, confirmedValues, inline]);

    useOnClickOutside(containerRef, handleClickOutside);
    useLockBodyScroll(!inline && shouldRenderPortal && isMobile);

    // Portal animasyon kontrolü
    useEffect(() => {
      if (inline) return;
      if (isOpen && isMobile) {
        setShouldRenderPortal(true);
        const timer = setTimeout(() => {
          setIsAnimating(true);
        }, 10);
        return () => clearTimeout(timer);
      } else if (!isOpen && isMobile) {
        setIsAnimating(false);
        const timer = setTimeout(() => {
          setShouldRenderPortal(false);
          onDropdownClose?.();
        }, 300);
        return () => clearTimeout(timer);
      }
    }, [isOpen, isMobile, inline]);

    // Web dropdown animasyon kontrolü
    useEffect(() => {
      if (inline) return;
      if (isOpen && !isMobile) {
        setShouldRenderWebDropdown(true);
        const timer = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsWebDropdownAnimating(true);
          });
        });
        return () => cancelAnimationFrame(timer);
      } else if (!isOpen && !isMobile) {
        setIsWebDropdownAnimating(false);
        const timer = setTimeout(() => {
          setShouldRenderWebDropdown(false);
          onDropdownClose?.();
        }, 200);
        return () => clearTimeout(timer);
      }
    }, [isOpen, isMobile, inline]);

    // Fire onDropdownOpen when dropdown opens (mobile portal or web dropdown)
    useEffect(() => {
      if (inline) return;
      const opened =
        (isMobile && shouldRenderPortal) ||
        (!isMobile && shouldRenderWebDropdown);
      if (opened) {
        onDropdownOpen?.();
      }
    }, [isMobile, shouldRenderPortal, shouldRenderWebDropdown, inline]);

    // Update internal state when external value prop changes
    useEffect(() => {
      if (value && Object.keys(value).length > 0) {
        const newValues = { ...defaultValues, ...value };
        setConfirmedValues(newValues);
        setTempValues(newValues);
      }
    }, [value, defaultValues]);

    // Validate whenever temp values change
    useEffect(() => {
      const result = validator(tempValues) || {};
      setValidationErrors(result.errors || {});
      setValidationWarnings(result.warnings || {});
      // Uygulama olmadan da parent'a state'i bildir
      if (onChange) {
        try {
          onChange(tempValues);
        } catch (e) {
          console.warn("onChange handler threw:", e);
        }
      }
    }, [tempValues, validator]);

    const handleValueChange = useCallback((slug, newValue) => {
      setTempValues((prev) => ({
        ...prev,
        [slug]: newValue,
      }));
    }, []);

    const handleApply = useCallback(() => {
      const result = validator(tempValues);

      if (result.isValid) {
        const settings = validatedConfig?.settings || {};
        if (typeof settings.applyValidation === "function") {
          const applyResult = settings.applyValidation(tempValues);
          if (!applyResult?.valid && onApplyValidationError) {
            onApplyValidationError();
            return;
          }
        }
        setConfirmedValues(tempValues);
        setIsOpen(false);
        onSelect?.(tempValues);
      }
    }, [
      tempValues,
      validator,
      onSelect,
      validatedConfig,
      onApplyValidationError,
    ]);

    const handlePortalClose = useCallback(() => {
      setIsOpen(false);
      setTempValues(confirmedValues);
    }, [confirmedValues]);

    const handleClick = useCallback(() => {
      if (inline) return;
      if (!isOpen) {
        setTempValues(confirmedValues);
      }
      setIsOpen(!isOpen);
    }, [isOpen, confirmedValues, inline]);

    // Imperative handle
    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setTempValues(confirmedValues);
          setIsOpen(true);
        },
        close: () => {
          setIsOpen(false);
          setTempValues(confirmedValues);
        },
        updateValues: (updates) => {
          setConfirmedValues((prev) => ({ ...prev, ...updates }));
          setTempValues((prev) => ({ ...prev, ...updates }));
        },
      }),
      [confirmedValues],
    );

    // Calculate display text
    const displayValue = useMemo(() => {
      return getDisplayText(validatedConfig, confirmedValues, displayFormatter);
    }, [validatedConfig, confirmedValues, displayFormatter]);

    // CSS classes
    const containerClasses = useMemo(
      () =>
        clsx(
          "selection-container border-primary-weak md:shadow-medium h-[68px] cursor-pointer border-b bg-white duration-300 ease-in-out md:h-[66px] md:border-0",
          isOpen && "md:ring-primary md:ring-2",
          !isMultiTrip ? "md:rounded-lg" : "rounded-lg",
        ),
      [isOpen, isMultiTrip],
    );

    const labelClasses = useMemo(
      () =>
        clsx(
          "selection-label text-foreground-muted pointer-events-none absolute left-[18px] transition-all duration-300 ease-in-out md:left-5",
          isOpen || displayValue
            ? "top-[11.5px] text-xs"
            : "top-1/2 -translate-y-1/2 text-base",
        ),
      [isOpen, displayValue],
    );

    const buttonClasses = useMemo(
      () =>
        clsx(
          "selection-button h-full w-full cursor-pointer truncate rounded-lg border-0 bg-transparent px-[18px] text-left font-medium transition-all duration-300 ease-in-out focus:ring-0 focus:outline-none md:px-5",
          isOpen || displayValue
            ? "pt-[26px] pb-[12px] md:pt-[31.5px] md:pb-[13.5px]"
            : "py-[12px] md:py-[22.5px]",
          buttonWrapperClassName,
        ),
      [isOpen, displayValue, buttonWrapperClassName],
    );

    const overlayClasses = useMemo(() => {
      return clsx(
        "fixed inset-0 z-50 bg-black/50",
        "transition-opacity duration-300",
        isAnimating ? "opacity-100" : "opacity-0",
      );
    }, [isAnimating]);

    const portalContainerClasses = useMemo(() => {
      return clsx(
        "fixed right-0 bottom-0 left-0 z-50 flex flex-col rounded-t-2xl bg-white",
        "transform transition-all duration-300",
        isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
      );
    }, [isAnimating]);

    const webDropdownClasses = useMemo(() => {
      return clsx(
        "shadow-xxl absolute top-full left-1/2 z-10 mt-2 w-[318px] -translate-x-1/2 rounded-lg bg-white p-0",
        "origin-top transform transition-all duration-200 ease-out",
        isWebDropdownAnimating
          ? "translate-y-0 scale-100 opacity-100"
          : "-translate-y-2 scale-95 opacity-0",
      );
    }, [isWebDropdownAnimating]);

    const isValid = Object.keys(validationErrors).length === 0;

    if (inline) {
      return (
        <div className={clsx("relative", className)} ref={containerRef}>
          {showTitle && label && (
            <div className="mb-3 text-sm font-semibold">{label}</div>
          )}
          <SelectionContent
            config={validatedConfig}
            values={tempValues}
            onValueChange={handleValueChange}
            onApply={handleApply}
            validationErrors={validationErrors}
            validationWarnings={validationWarnings}
            isValid={isValid}
            t={t}
            applyButtonText={applyButtonText}
            showApplyButton={showApplyButton}
            generalErrorsPosition={generalErrorsPosition}
            renderHeader={renderHeader}
            headerClassName={headerClassName}
            renderFooter={renderFooter}
            selectionItemsClassName={selectionItemsClassName}
            footerClassName={footerClassName}
            contextProps={{
              tempValues,
              setTempValues,
              confirmedValues,
              isOpen: true,
              isValid,
              apply: handleApply,
              cancel: handlePortalClose,
            }}
          />
        </div>
      );
    }

    return (
      <>
        <div className={clsx("relative", className)} ref={containerRef}>
          <div className={containerClasses} onClick={handleClick}>
            {label && <label className={labelClasses}>{label}</label>}
            <button type="button" className={buttonClasses}>
              {displayValue || placeholder}
            </button>
          </div>

          {/* Web dropdown */}
          {!isMobile && shouldRenderWebDropdown && (
            <div className={webDropdownClasses}>
              <SelectionContent
                config={validatedConfig}
                values={tempValues}
                onValueChange={handleValueChange}
                onApply={handleApply}
                validationErrors={validationErrors}
                validationWarnings={validationWarnings}
                isValid={isValid}
                t={t}
                applyButtonText={applyButtonText}
                showApplyButton={showApplyButton}
                generalErrorsPosition={generalErrorsPosition}
                renderHeader={renderHeader}
                headerClassName={headerClassName}
                renderFooter={renderFooter}
                selectionItemsClassName={selectionItemsClassName}
                footerClassName={footerClassName}
                contextProps={{
                  tempValues,
                  setTempValues,
                  confirmedValues,
                  isOpen,
                  isValid,
                  apply: handleApply,
                  cancel: handlePortalClose,
                }}
              />
            </div>
          )}
        </div>

        {/* Mobile portal */}
        {shouldRenderPortal &&
          isMobile &&
          createPortal(
            <>
              <div className={overlayClasses} onClick={handlePortalClose} />
              <div className={portalContainerClasses}>
                {/* Header */}
                {showTitle && (
                  <div className="flex shrink-0 items-center justify-between px-4 py-5">
                    <span className="w-full text-center text-lg font-semibold">
                      {label}
                    </span>
                    <button onClick={handlePortalClose} className="self-end">
                      <FontAwesomeIcon
                        icon={byPrefixAndName.far["xmark"]}
                        className="text-2xl"
                      />
                    </button>
                  </div>
                )}

                {/* Content */}
                <div className="border-primary-weak flex-1 overflow-y-auto">
                  <SelectionContent
                    config={validatedConfig}
                    values={tempValues}
                    onValueChange={handleValueChange}
                    onApply={handleApply}
                    validationErrors={validationErrors}
                    validationWarnings={validationWarnings}
                    isValid={isValid}
                    t={t}
                    applyButtonText={applyButtonText}
                    showApplyButton={showApplyButton}
                    generalErrorsPosition={generalErrorsPosition}
                    renderHeader={renderHeader}
                    headerClassName={headerClassName}
                    renderFooter={renderFooter}
                    selectionItemsClassName={selectionItemsClassName}
                    footerClassName={footerClassName}
                    contextProps={{
                      tempValues,
                      setTempValues,
                      confirmedValues,
                      isOpen,
                      isValid,
                      apply: handleApply,
                      cancel: handlePortalClose,
                    }}
                  />
                </div>
              </div>
            </>,
            document.body,
          )}
      </>
    );
  },
);

// Selection content component
const SelectionContent = ({
  config,
  values,
  onValueChange,
  onApply,
  validationErrors,
  validationWarnings = {},
  isValid,
  t,
  applyButtonText,
  showApplyButton,
  generalErrorsPosition = "top",
  renderHeader,
  renderFooter,
  selectionItemsClassName,
  footerClassName,
  contextProps,
  headerClassName,
}) => {
  const renderItem = (item, index) => {
    // Skip hidden items
    if (item.hidden) return null;

    const wrap = (node) =>
      item?.className ? <div className={item.className}>{node}</div> : node;

    switch (item.type) {
      case SELECTION_TYPES.INCREMENT:
        return wrap(
          <IncrementSelector
            key={item.slug || index}
            item={item}
            value={values[item.slug] || item.value || 0}
            onChange={(newValue) => onValueChange(item.slug, newValue)}
            error={validationErrors[item.slug]}
            t={t}
          />,
        );

      case SELECTION_TYPES.GROUP:
        const groupLabel = getTranslatedText(item.label, item.labelKey, t);
        return (
          <div key={index} className={item.className}>
            {groupLabel && (
              <div className="border-primary-weak border-b p-4 font-semibold">
                {groupLabel}
              </div>
            )}
            {item.children?.map((child, childIndex) =>
              renderItem(child, childIndex),
            )}
          </div>
        );

      case SELECTION_TYPES.RADIO:
        return wrap(
          <RadioSelector
            key={item.slug || index}
            item={item}
            value={values[item.slug]}
            onChange={(newValue) => onValueChange(item.slug, newValue)}
            t={t}
          />,
        );

      case SELECTION_TYPES.CHECKBOX:
        return wrap(
          <CheckboxSelector
            key={item.slug || index}
            item={item}
            value={values[item.slug] || false}
            onChange={(newValue) => onValueChange(item.slug, newValue)}
            t={t}
          />,
        );

      case SELECTION_TYPES.TOGGLE:
        return wrap(
          <ToggleSelector
            key={item.slug || index}
            item={item}
            value={values[item.slug] || false}
            onChange={(newValue) => onValueChange(item.slug, newValue)}
            t={t}
          />,
        );

      default:
        return null;
    }
  };
  return (
    <div className="flex flex-col">
      {/* General validation errors (top) */}
      {generalErrorsPosition === "top" && validationErrors.general && (
        <div className="px-3 py-4">
          <GeneralErrorBox error={validationErrors.general} t={t} />
        </div>
      )}
      {generalErrorsPosition === "top" && validationWarnings.general && (
        <div className="px-3 py-4">
          <GeneralWarningBox warning={validationWarnings.general} t={t} />
        </div>
      )}

      {/* Render header */}
      {typeof renderHeader === "function" && (
        <div
          className={clsx(
            "selection-header border-primary-weak border-b",
            headerClassName,
          )}
        >
          {renderHeader(contextProps)}
        </div>
      )}

      {/* Render all items */}
      <div className={clsx("selection-items", selectionItemsClassName)}>
        {config.children?.map((item, index) => renderItem(item, index))}
      </div>

      {/* Render footer */}
      {typeof renderFooter === "function" && (
        <div
          className={clsx(
            "selection-footer border-primary-weak",
            contextProps.tempValues.child === 0 && "border-t",
            footerClassName,
          )}
        >
          {renderFooter(contextProps)}
        </div>
      )}

      {/* General validation errors (bottom) */}
      {generalErrorsPosition === "bottom" && validationErrors.general && (
        <div className="p-3">
          <GeneralErrorBox error={validationErrors.general} t={t} />
        </div>
      )}
      {generalErrorsPosition === "bottom" && validationWarnings.general && (
        <div className="p-3">
          <GeneralWarningBox warning={validationWarnings.general} t={t} />
        </div>
      )}

      {/* Apply button */}
      {showApplyButton && (
        <div className="p-4">
          <Button
            onClick={onApply}
            disabled={!isValid}
            size="lg"
            className="w-full"
          >
            {applyButtonText}
          </Button>
        </div>
      )}
    </div>
  );
};

// Increment selector component
const IncrementSelector = ({ item, value, onChange, error, t }) => {
  const handleDecrement = () => {
    const newValue = Math.max(item.min ?? 0, value - 1);
    onChange(newValue);
  };

  const handleIncrement = () => {
    const newValue = Math.min(item.max ?? 999, value + 1);
    onChange(newValue);
  };

  const label = getTranslatedText(item.label, item.labelKey, t);
  const description = getTranslatedText(
    item.description,
    item.descriptionKey,
    t,
  );
  const badge = getTranslatedText(item.badge, item.badgeKey, t);

  return (
    <div
      className={clsx(
        "border-primary-weak flex h-[72px] items-center justify-between p-4 md:h-[76px]",
        item.disabled && "opacity-50",
        error && "bg-red-50",
      )}
    >
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <span className="font-medium">{label}</span>
          {badge && (
            <span className="bg-primary-weak text-primary rounded px-2 py-0.5 text-xs font-medium">
              {badge}
            </span>
          )}
        </div>
        {description && (
          <span className="text-foreground-muted text-sm">{description}</span>
        )}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={handleDecrement}
          disabled={item.disabled || value <= (item.min ?? 0)}
          className="bg-primary disabled:text-foreground-muted disabled:bg-background-500 relative flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-white transition-colors disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon
            icon={byPrefixAndName.fak["minus"]}
            className="relative text-[20px]"
          />
        </button>
        <span className="text-foreground-default w-5 text-center text-base leading-21 font-medium">
          {value}
        </span>
        <button
          onClick={handleIncrement}
          disabled={item.disabled || value >= (item.max ?? 999)}
          className="bg-primary disabled:text-foreground-muted disabled:bg-background-500 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-white transition-colors disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon
            icon={byPrefixAndName.fak["plus"]}
            className="text-[20px]"
          />
        </button>
      </div>
    </div>
  );
};

// Radio selector component
const RadioSelector = ({ item, value, onChange, t }) => {
  const label = getTranslatedText(item.label, item.labelKey, t);

  return (
    <div className="border-primary-weak border-b p-4">
      <div className="mb-2 font-medium">{label}</div>
      <div className="flex gap-2">
        {item.children?.map((option) => {
          const optionLabel = getTranslatedText(
            option.label,
            option.labelKey,
            t,
          );
          return (
            <button
              key={option.slug}
              onClick={() => onChange(option.slug)}
              disabled={item.disabled}
              className={clsx(
                "flex-1 cursor-pointer rounded-lg border px-4 py-3 text-center transition-colors",
                value === option.slug
                  ? "border-primary bg-primary-weak text-primary"
                  : "border-primary-weak hover:border-primary hover:bg-primary-weak",
                item.disabled && "cursor-not-allowed opacity-50",
              )}
            >
              {optionLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Checkbox selector component
const CheckboxSelector = ({ item, value, onChange, t }) => {
  const label = getTranslatedText(item.label, item.labelKey, t);
  const description = getTranslatedText(
    item.description,
    item.descriptionKey,
    t,
  );

  return (
    <div className="border-primary-weak border-b p-4">
      <label className="inline-flex w-auto cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          disabled={item.disabled}
          className="text-primary focus:ring-primary border-border-strong ring-none h-6 w-6 rounded-[6px] border-[1.2px] outline-0 transition-all duration-200 ease-in-out"
        />
        <span>{label}</span>
        {description && (
          <span className="text-foreground-muted text-sm">({description})</span>
        )}
      </label>
    </div>
  );
};

// Toggle selector component
const ToggleSelector = ({ item, value, onChange, t }) => {
  const label = getTranslatedText(item.label, item.labelKey, t);
  const description = getTranslatedText(
    item.description,
    item.descriptionKey,
    t,
  );

  return (
    <div className="border-primary-weak flex items-center justify-between p-4">
      <div>
        <div className="font-medium">{label}</div>
        {description && (
          <div className="text-foreground-muted text-sm">{description}</div>
        )}
      </div>
      <button
        onClick={() => onChange(!value)}
        disabled={item.disabled}
        className={clsx(
          "relative h-6 w-11 rounded-full transition-colors",
          value ? "bg-primary" : "bg-gray-300",
          item.disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <span
          className={clsx(
            "absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform",
            value ? "translate-x-5" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
};

GenericSelectionModal.displayName = "GenericSelectionModal";

export default GenericSelectionModal;

// General error box component
const GeneralErrorBox = ({ error, t }) => {
  // error can be string or structured object
  // Structured: { titleKey?, title?, descriptionKey?, description?, icon?: 'info'|'warning', params?: Record }
  const isObject = error && typeof error === "object";
  const titleText = isObject
    ? getTranslatedText(error.title, error.titleKey, t)
    : error;
  const descriptionText = isObject
    ? getTranslatedText(error.description, error.descriptionKey, t)
    : null;

  if (!titleText && !descriptionText) {
    return null;
  }

  return (
    <div
      className="flex items-start gap-2 rounded-[10px] border border-[#FED7AA] bg-[#FFF7ED] p-4"
      role="alert"
    >
      <span className="mt-0.5 !h-6 !w-6 text-[#C2410C]">
        <FontAwesomeIcon
          className="!h-5 !w-5"
          icon={byPrefixAndName.fas["circle-info"]}
        />
      </span>
      <div className="flex flex-col">
        <div className="font-semibold text-[#9A3412]">{titleText}</div>
        {descriptionText && (
          <div className="text-sm text-[#9A3412]">{descriptionText}</div>
        )}
      </div>
    </div>
  );
};

// General warning box component
const GeneralWarningBox = ({ warning, t }) => {
  const isObject = warning && typeof warning === "object";
  const titleText = isObject
    ? getTranslatedText(warning.title, warning.titleKey, t)
    : warning;
  const descriptionText = isObject
    ? getTranslatedText(warning.description, warning.descriptionKey, t)
    : null;

  if (!titleText && !descriptionText) {
    return null;
  }

  return (
    <div
      className="border-danger-subtle bg-danger-weaker flex items-start gap-[10px] rounded-lg border p-4"
      role="alert"
    >
      <span className="text-danger mt-0.5 !h-6 !w-6">
        <FontAwesomeIcon
          className="!h-5 !w-5"
          icon={byPrefixAndName.far["circle-exclamation"]}
        />
      </span>
      <div className="flex flex-col">
        <div className="text-foreground-default font-semibold">{titleText}</div>
        {descriptionText && (
          <div className="text-foreground-default text-sm">
            {descriptionText}
          </div>
        )}
      </div>
    </div>
  );
};
